import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom';
import { useBrand } from '../brand/BrandProvider';

/**
 * Root-route errorElement — the last line of defense against an unhandled
 * render-time throw (e.g. a malformed record crashing a list page's sort).
 * Without this, react-router's own default error page renders instead,
 * which dumps a raw stack trace straight to the user in production.
 */
export function ErrorFallback() {
  const error = useRouteError();
  const navigate = useNavigate();
  const { shortName, kicker } = useBrand();
  const detail = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
      ? error.stack ?? error.message
      : String(error);

  return (
    <div className="flex min-h-screen flex-col bg-cream items-center justify-center px-6">
      <div className="bg-surface border border-line shadow-panel px-12 py-14 w-full max-w-[440px] text-center">
        <div className="mb-8">
          {kicker && (
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate font-sans mb-2">
              {kicker}
            </div>
          )}
          <div className="font-display text-[30px] font-semibold uppercase tracking-[0.16em] text-navy mb-3">
            {shortName}
          </div>
          <div className="w-8 h-px bg-bronze mx-auto" />
        </div>

        <p className="text-[13px] text-navy m-0 mb-2 leading-relaxed font-sans font-semibold">
          Something went wrong.
        </p>
        <p className="text-[13px] text-slate m-0 mb-9 leading-relaxed font-sans">
          Something on this page didn't load correctly. Try again, or return to Overview.
        </p>

        <div className="flex gap-3 justify-center">
          <button
            onClick={() => window.location.reload()}
            className="px-5 py-[13px] bg-navy text-cream border-none rounded-token text-[11px] font-bold font-sans uppercase tracking-[0.12em] cursor-pointer"
          >
            Reload
          </button>
          <button
            onClick={() => navigate('/', { replace: true })}
            className="px-5 py-[13px] bg-transparent text-navy border border-line-strong rounded-token text-[11px] font-bold font-sans uppercase tracking-[0.12em] cursor-pointer"
          >
            Go to Overview
          </button>
        </div>

        {import.meta.env.DEV && (
          <pre className="mt-8 text-left text-[10px] text-danger/80 whitespace-pre-wrap break-words bg-danger/5 border border-danger/15 rounded-token p-3 max-h-[240px] overflow-auto">
            {detail}
          </pre>
        )}
      </div>
    </div>
  );
}
